import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { money, fmtDate, isPast } from './util';
import EventDetail from './EventDetail';
import CreateEventModal from './CreateEventModal';

export default function EventsModule() {
  const api = useApi();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  // El evento abierto va en la URL (?ev=…): así refrescar te deja donde estabas y el
  // botón "atrás" del navegador vuelve a la lista.
  const [params, setParams] = useSearchParams();
  const openEventId = params.get('ev') || null;
  const setOpenEventId = (id) => {
    const next = new URLSearchParams(params);
    if (id) next.set('ev', id); else { next.delete('ev'); next.delete('t'); }
    setParams(next, { replace: true });
  };
  const [creating, setCreating] = useState(false);
  const [pend, setPend] = useState(null);   // cobros pendientes de RRPP, por evento
  // El archivo también va en la URL (?arch=1&a=2026&m=6), por lo mismo: refrescar ahí
  // adentro no tiene que devolverte a la lista.
  const verArchivo = params.get('arch') === '1';
  const setVerArchivo = (v) => {
    const next = new URLSearchParams(params);
    if (v) next.set('arch', '1'); else { next.delete('arch'); next.delete('a'); next.delete('m'); }
    setParams(next, { replace: true });
  };
  // El período elegido en el archivo también viaja en la URL: si no, abrir un evento y
  // volver (o refrescar) te devolvía al año en curso, perdiendo el mes que mirabas.
  const periodo = { anio: params.get('a') ? +params.get('a') : null, mes: params.get('m') || '' };
  const setPeriodo = ({ anio, mes }) => {
    const next = new URLSearchParams(params);
    if (anio) next.set('a', String(anio)); else next.delete('a');
    if (mes) next.set('m', String(mes)); else next.delete('m');
    setParams(next, { replace: true });
  };

  const load = useCallback(async () => {
    try {
      const data = await api.get('/api/events');
      setEvents(data);
    } catch (e) { /* noop */ }
    setLoading(false);
  }, []); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  // Cobros pendientes de RRPP. Se pide aparte de los eventos para que un error acá no
  // deje al dueño sin ver sus eventos: el aviso es importante, la lista es la pantalla.
  useEffect(() => {
    api.get('/api/rrpp-renditions/pendientes').then(setPend).catch(() => {});
  }, []); // eslint-disable-line

  const pendDe = (id) => (pend?.eventos || []).find(x => x.event_id === id) || null;

  const deleteEvent = async (ev) => {
    if (!window.confirm(`¿Eliminar el evento "${ev.name}"? Se borran sus lotes, entradas y datos. Esta acción no se puede deshacer.`)) return;
    try { await api.del(`/api/events/${ev.id}`); load(); }
    catch (e) { alert(e.message); }
  };

  if (openEventId) {
    return (
      <EventDetail
        eventId={openEventId}
        onBack={() => { setOpenEventId(null); load(); }}
      />
    );
  }

  const upcoming = events.filter(e => !isPast(e));
  const finished = events.filter(e => isPast(e));

  // ARCHIVO: la lista de finalizados crece para siempre. En la pantalla principal van los
  // últimos 8 (lo que se mira de verdad: la noche pasada, la anterior) y el resto vive acá,
  // buscado por mes.
  if (verArchivo) {
    return (
      <Archivo
        finalizados={finished}
        periodo={periodo}
        setPeriodo={setPeriodo}
        pendDe={pendDe}
        onAbrir={setOpenEventId}
        onBorrar={deleteEvent}
        onVolver={() => setVerArchivo(false)}
      />
    );
  }

  const ultimos = finished.slice(0, 8);

  return (
    <div className="lb-fade">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div className="lb-eyebrow">{events.length} eventos</div>
        <button className="lb-btn lb-btn--primary lb-btn--sm" onClick={() => setCreating(true)}>+ Crear evento</button>
      </div>

      {/* AVISO DE COBROS PENDIENTES. Va acá arriba y no en un cartel flotante: un modal
          al entrar se cierra por reflejo y el aviso deja de verse a la semana. Este queda
          quieto en la pantalla hasta que la plata esté cobrada, y cada evento además
          muestra su propia chapa para saber a cuál ir. */}
      {pend && pend.total_rrpp > 0 && (
        <div className="lb-card" style={{ padding: '16px 20px', marginBottom: 22, border: '1px solid rgba(212,175,55,0.55)', background: 'linear-gradient(180deg, rgba(212,175,55,0.10), rgba(212,175,55,0.02))' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 20, lineHeight: 1 }}>💰</span>
            <span style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 17, color: '#fff' }}>
              {money(pend.total_monto)} sin rendir de RRPP
            </span>
            <span style={{ fontSize: 12.5, color: 'var(--ink-700)' }}>
              · {pend.total_rrpp} {pend.total_rrpp === 1 ? 'rendición' : 'rendiciones'} en {pend.eventos.length} {pend.eventos.length === 1 ? 'evento' : 'eventos'}
            </span>
          </div>
          <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
            {pend.eventos.slice(0, 5).map(e => (
              <button key={e.event_id} onClick={() => setOpenEventId(e.event_id)}
                style={{ background: 'transparent', border: 0, padding: 0, cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit', fontSize: 12.5, color: 'var(--ink-800)' }}>
                <b style={{ color: '#fff' }}>{e.event_name}</b>
                <span style={{ color: 'var(--ink-600)' }}> · {fmtDate(e.event_date)} · </span>
                {e.sin_rendir > 0 && <span style={{ color: 'var(--red-400)' }}>{e.sin_rendir} sin rendir ({money(e.monto_sin_rendir)})</span>}
                {e.sin_rendir > 0 && e.a_revisar > 0 && <span style={{ color: 'var(--ink-600)' }}> · </span>}
                {e.a_revisar > 0 && <span style={{ color: '#ffb300' }}>{e.a_revisar} a revisar ({money(e.monto_a_revisar)})</span>}
                <span style={{ color: 'var(--ink-600)' }}> → abrir</span>
              </button>
            ))}
            {pend.eventos.length > 5 && <span style={{ fontSize: 11.5, color: 'var(--ink-600)' }}>y {pend.eventos.length - 5} evento(s) más.</span>}
          </div>
          <div style={{ fontSize: 11, color: 'var(--ink-600)', marginTop: 10 }}>
            Lo que falta sale de la <b>planilla de Administración</b> (a rendir menos lo cargado en efectivo y digital), no de si subió comprobante: si rindió en efectivo y lo cargaste, no aparece. <b>Sin rendir</b> = falta plata y no subió nada. <b>A revisar</b> = falta plata y subió un comprobante para que lo cargues. Solo noches que ya pasaron.
          </div>
        </div>
      )}

      {loading && <div style={{ color: 'var(--ink-600)', fontSize: 13 }}>Cargando eventos…</div>}

      {!loading && events.length === 0 && (
        <div style={{ padding: '60px 20px', textAlign: 'center', border: '1px dashed var(--border)', borderRadius: 4 }}>
          <div style={{ color: 'var(--ink-600)', fontSize: 14, marginBottom: 16 }}>Todavía no hay eventos.</div>
          <button className="lb-btn lb-btn--primary lb-btn--sm" onClick={() => setCreating(true)}>+ Crear el primero</button>
        </div>
      )}

      {upcoming.length > 0 && (
        <div className="ev-grid">
          {upcoming.map(ev => <EventCard key={ev.id} ev={ev} pend={pendDe(ev.id)} onOpen={() => setOpenEventId(ev.id)} onDelete={() => deleteEvent(ev)} />)}
        </div>
      )}

      {finished.length > 0 && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, margin: '40px 0 20px' }}>
            <span className="lb-eyebrow">Finalizados</span>
            <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.1)' }} />
          </div>
          <div className="ev-grid">
            {ultimos.map(ev => <EventCard key={ev.id} ev={ev} pend={pendDe(ev.id)} onOpen={() => setOpenEventId(ev.id)} onDelete={() => deleteEvent(ev)} past />)}
          </div>
          {finished.length > ultimos.length && (
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: 22 }}>
              <button className="lb-btn lb-btn--ghost lb-btn--sm" onClick={() => setVerArchivo(true)}>
                Ver todos los finalizados ({finished.length}) →
              </button>
            </div>
          )}
        </>
      )}

      {creating && (
        <CreateEventModal
          api={api}
          onClose={() => setCreating(false)}
          onCreated={(ev) => { setCreating(false); load(); setOpenEventId(ev.id); }}
        />
      )}
    </div>
  );
}

const MESES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

// ARCHIVO DE EVENTOS FINALIZADOS, buscados por mes.
//
// La fecha se parte del texto (YYYY-MM-DD) y no con new Date(): con Date, un evento del
// día 1 a la medianoche se iba al mes anterior según la zona horaria del navegador.
function mesDe(ev) {
  const dia = String(ev.date || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dia)) return null;
  return { anio: +dia.slice(0, 4), mes: +dia.slice(5, 7) };
}

function Archivo({ finalizados, periodo, setPeriodo, pendDe, onAbrir, onBorrar, onVolver }) {
  const conFecha = finalizados.map(ev => ({ ev, m: mesDe(ev) })).filter(x => x.m);
  const anios = [...new Set(conFecha.map(x => x.m.anio))].sort((a, b) => b - a);
  // Sin elección previa arranca en el año del evento más reciente: es donde está lo que
  // se busca al entrar.
  const anio = periodo.anio ?? (anios[0] || new Date().getFullYear());
  const mes = periodo.mes;   // '' = todo el año
  const setAnio = (a) => setPeriodo({ anio: a, mes: '' });
  const setMes = (m) => setPeriodo({ anio, mes: m });

  const delAnio = conFecha.filter(x => x.m.anio === anio);
  const visibles = (mes === '' ? delAnio : delAnio.filter(x => x.m.mes === +mes)).map(x => x.ev);
  // Los meses que no tuvieron ninguna noche no se ofrecen: elegirlos era caer en vacío.
  const mesesConEventos = [...new Set(delAnio.map(x => x.m.mes))].sort((a, b) => a - b);
  const sinFecha = finalizados.filter(ev => !mesDe(ev));

  return (
    <div className="lb-fade">
      <button className="lb-btn lb-btn--ghost lb-btn--sm" onClick={onVolver}>← Volver a eventos</button>

      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', margin: '20px 0 22px' }}>
        <div>
          <div className="lb-display" style={{ fontSize: 24 }}>Eventos finalizados</div>
          <p style={{ color: 'var(--ink-600)', fontSize: 13, marginTop: 4 }}>
            {finalizados.length} noches en total. Elegí el mes que querés mirar.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <select className="lb-select" style={{ width: 120 }} value={anio} onChange={e => setAnio(+e.target.value)}>
            {anios.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
          <select className="lb-select" style={{ width: 170 }} value={mes} onChange={e => setMes(e.target.value)}>
            <option value="">Todo el año</option>
            {mesesConEventos.map(m => <option key={m} value={m}>{MESES[m - 1]}</option>)}
          </select>
        </div>
      </div>

      <div className="lb-eyebrow" style={{ marginBottom: 14 }}>
        {visibles.length} {visibles.length === 1 ? 'evento' : 'eventos'} · {mes === '' ? `todo ${anio}` : `${MESES[+mes - 1]} ${anio}`}
      </div>

      {visibles.length === 0
        ? <div style={{ padding: '40px 20px', textAlign: 'center', border: '1px dashed var(--border)', borderRadius: 4, color: 'var(--ink-600)', fontSize: 13 }}>No hubo eventos en ese período.</div>
        : (
          <div className="ev-grid">
            {visibles.map(ev => <EventCard key={ev.id} ev={ev} pend={pendDe(ev.id)} onOpen={() => onAbrir(ev.id)} onDelete={() => onBorrar(ev)} past />)}
          </div>
        )}

      {/* Un evento sin fecha no entra en ningún mes: si no se mostrara acá, no habría forma
          de llegar a él desde ninguna pantalla. */}
      {sinFecha.length > 0 && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, margin: '36px 0 18px' }}>
            <span className="lb-eyebrow">Sin fecha</span>
            <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.1)' }} />
          </div>
          <div className="ev-grid">
            {sinFecha.map(ev => <EventCard key={ev.id} ev={ev} pend={pendDe(ev.id)} onOpen={() => onAbrir(ev.id)} onDelete={() => onBorrar(ev)} past />)}
          </div>
        </>
      )}
    </div>
  );
}

function EventCard({ ev, pend, onOpen, onDelete, past }) {
  const m = ev.metrics || { vendidas: 0, recaudado: 0, ingresaron: 0 };
  return (
    <div className="ev-card" onClick={onOpen} style={{ opacity: past ? 0.78 : 1, position: 'relative' }}>
      {/* watermark logo */}
      <img src="/assets/tukipass-icon.png" alt="" className="lb-watermark" style={{ width: 180, right: -30, bottom: -30 }} />

      {/* Chapa de cobros pendientes sobre el flyer: es lo que hace que no se pase. El
          evento finalizado se dibuja atenuado, así que la chapa lleva su propio fondo
          opaco para que no se pierda justo en las noches viejas, que son las que más se
          olvidan. */}
      {pend && (pend.sin_rendir + pend.a_revisar) > 0 && (
        <div style={{ position: 'absolute', top: 10, right: 10, zIndex: 2, background: 'rgba(0,0,0,0.82)', border: '1px solid ' + (pend.sin_rendir > 0 ? 'var(--red-400)' : '#ffb300'), borderRadius: 999, padding: '5px 11px', display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 12 }}>💰</span>
          <span style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 12, color: pend.sin_rendir > 0 ? 'var(--red-400)' : '#ffb300' }}>
            {money(pend.monto_sin_rendir + pend.monto_a_revisar)}
          </span>
          <span style={{ fontSize: 10, color: 'var(--ink-700)' }}>
            {pend.sin_rendir > 0 ? `${pend.sin_rendir} sin rendir` : `${pend.a_revisar} a revisar`}
          </span>
        </div>
      )}

      {/* flyer or gradient header */}
      <div style={{ height: 120, background: ev.flyer_url ? `center/cover no-repeat url(${ev.flyer_url})` : 'linear-gradient(135deg, var(--red-900), var(--ink-200))', position: 'relative', borderBottom: '1px solid var(--border)' }}>
        <button
          onClick={(e) => { e.stopPropagation(); onDelete && onDelete(); }}
          title="Eliminar evento"
          style={{ position: 'absolute', top: 10, left: 10, width: 30, height: 30, borderRadius: 4, border: '1px solid rgba(0,0,0,0.4)', background: 'rgba(0,0,0,0.55)', color: '#fff', cursor: 'pointer', fontSize: 14, lineHeight: 1, display: 'grid', placeItems: 'center' }}
        >✕</button>
        <span style={{ position: 'absolute', top: 12, right: 12 }}
          className={'lb-status lb-status--dot ' + (ev.published ? 'lb-status--paid' : 'lb-status--gen')}>
          {ev.published ? 'Publicado' : 'Oculto'}
        </span>
      </div>

      <div style={{ padding: '18px 20px', position: 'relative' }}>
        <div style={{ fontSize: 11, color: 'var(--ink-600)', textTransform: 'capitalize' }}>{fmtDate(ev.date)}</div>
        <div className="lb-h" style={{ fontSize: 19, marginTop: 4 }}>{ev.name}</div>
        {ev.venue && <div style={{ fontSize: 12, color: 'var(--ink-700)', marginTop: 4 }}>{ev.venue}</div>}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8, marginTop: 18, paddingTop: 16, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
          <Metric label="Vendidas" value={m.vendidas} />
          <Metric label="Recaudado" value={money(m.recaudado)} small />
          <Metric label="Ingresaron" value={m.ingresaron} />
        </div>
      </div>
    </div>
  );
}

function Metric({ label, value, small }) {
  return (
    <div>
      <div style={{ fontSize: 9, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--ink-600)' }}>{label}</div>
      <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: small ? 14 : 20, color: '#fff', marginTop: 3 }}>{value}</div>
    </div>
  );
}
